<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';

import { performanceMark } from '@libs/utils';

import {
  useBrowserStore,
  useRouterStore,
  useStatsigEventsStore,
} from '../../core/stores';
import { useRecipeStore } from '../../core/stores/recipe/recipeStore';

import HotelSelectionContentDesktop from './components/HotelSelectionContentDesktop.vue';
import HotelSelectionContentMobile from './components/HotelSelectionContentMobile.vue';
import HotelSelectionDesktopLayout from './components/HotelSelectionDesktopLayout.vue';
import HotelSelectionHeaderDesktop from './components/HotelSelectionHeaderDesktop.vue';
import HotelSelectionHeaderMobile from './components/HotelSelectionHeaderMobile.vue';
import HotelSelectionMobileLayout from './components/HotelSelectionMobileLayout.vue';
import HotelSelectionModals from './components/HotelSelectionModals.vue';
import ListMapSwitch from './components/ListMapSwitch.vue';
import { HotelSelectionViewType } from './models';
import { useHotelSelectionStore, useHotelSelectionMapStore } from './stores';

const browserStore = useBrowserStore();
const hotelSelectionStore = useHotelSelectionStore();
const hotelSelectionMapStore = useHotelSelectionMapStore();
const route = useRoute();
const routerStore = useRouterStore();
const recipeStore = useRecipeStore();
const statsigEventsStore = useStatsigEventsStore();

onMounted(async () => {
  performanceMark('HotelSelectionViewInitiated');
  // HACK: scroll top to avoid iOS Safari address bar issue
  // When users have scroll bar on top in iOS Safari, safari wrongly renders hotel selection page when navigating from other pages.
  // Forcing app to sroll top 1px will fix the issue.
  if (browserStore.isSmallerThan600px) {
    setTimeout(() => {
      window.scrollTo(0, 1);
    }, 0);
  }

  const index = route.params.ingredientIndex;

  if (typeof index === 'string') {
    const indexAsNumber = parseInt(index);

    if (isNaN(indexAsNumber)) {
      const regex = /^h\d+$/;
      if (regex.test(index)) {
        const stayIndex = index.slice(1);
        const stayIndexAsNumber = parseInt(stayIndex);
        hotelSelectionStore.initializeHotelSelectionView(stayIndexAsNumber);
      } else {
        await routerStore.navigateToTripOverview();
        statsigEventsStore.logHotelSelectionWithWrongIndexAccessed();
      }
    } else {
      const stayOnlyIndex =
        recipeStore.mapRecipeIndexToStayOnlyIndex(indexAsNumber);

      if (stayOnlyIndex !== undefined) {
        await routerStore.navigateToHotelSelection({ stayOnlyIndex });
        hotelSelectionStore.initializeHotelSelectionView(stayOnlyIndex);
        statsigEventsStore.logHotelSelectionWithOldIndexAccessed();
      } else {
        // HACK: Navigate here is twice, otherise is does not work,
        // due to redirect from XX number of nights to number of nights fetched from BE.
        // Clean up task: https://trello.com/c/JOYlFnlj/4598-clean-up-after-migration-to-stay-only-indexes-in-hotel-selection
        await routerStore.navigateToTripOverview();
        await routerStore.navigateToTripOverview();
        statsigEventsStore.logHotelSelectionWithWrongIndexAccessed();
      }
    }
  }
  hotelSelectionMapStore.destroyMap(); // HACK: This is a way to fix zooming out map in hotel selection page https://trello.com/c/DvEarbjd
});

onUnmounted(() => {
  hotelSelectionStore.resetHotelSelectionView();
});

const hasInitializeHotelSelectionList = ref<boolean>(false);
const isOpeningPriceCalendar = ref<boolean>(false);

function onInitializeHotelSelectionList(): void {
  if (hasInitializeHotelSelectionList.value) {
    return;
  }
  hotelSelectionStore.handleInitializedEvent();
  hasInitializeHotelSelectionList.value = true;
}

function onShowPriceRibbon(): void {
  isOpeningPriceCalendar.value = true;
  hotelSelectionStore.showPriceRibbon();

  // HACK: block long scroll event until price calendar is fully opened
  const PRICE_CALENDAR_TRANSITION_TIME_IN_MS = 500; // price calendar transition duration
  setTimeout(() => {
    isOpeningPriceCalendar.value = false;
  }, PRICE_CALENDAR_TRANSITION_TIME_IN_MS);
}

function onLongScroll(): void {
  if (isOpeningPriceCalendar.value) {
    return;
  }
  hotelSelectionStore.hidePriceRibbon();
}
</script>

<template>
  <!-- NOTE: Hotel details panel is mounted via child route -->
  <router-view />

  <HotelSelectionModals />

  <HotelSelectionMobileLayout
    v-if="browserStore.isSmallerThan1200px"
    :has-fixed-height="
      hotelSelectionStore.viewType === HotelSelectionViewType.Map
    "
    @long-scroll="onLongScroll"
  >
    <template #header>
      <HotelSelectionHeaderMobile @show-price-ribbon="onShowPriceRibbon" />
    </template>
    <template #content>
      <HotelSelectionContentMobile
        @initialized-hotel-selection-list="onInitializeHotelSelectionList()"
      />
    </template>
    <template #list-map-switch>
      <ListMapSwitch />
    </template>
  </HotelSelectionMobileLayout>

  <HotelSelectionDesktopLayout v-else>
    <template #upper-panel>
      <HotelSelectionHeaderDesktop />
    </template>
    <template #lower-panel>
      <HotelSelectionContentDesktop
        @initialized-hotel-selection-list="onInitializeHotelSelectionList()"
      />
    </template>
  </HotelSelectionDesktopLayout>
</template>
